import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "./api";

export const ClientConfigContext = createContext();

/**
 * Provides client-level configuration (e.g. terminology choices) to the whole app.
 * Reads from / writes to the existing /owner-dashboard-settings endpoint so no
 * new backend table is needed.
 *
 * Exposes:
 *   config           . raw settings object
 *   isHeritage       . true when work_order_terminology === "heritage"
 *   terminology      . "standard" | "heritage"
 *   updateTerminology(val). save a new value (updates DB + local state)
 */
export const ClientConfigProvider = ({ children }) => {
  const [terminology, setTerminology] = useState(() => {
    // Hydrate from localStorage immediately so there's no flash on reload
    try {
      return localStorage.getItem("client_terminology") || "standard";
    } catch {
      return "standard";
    }
  });

  const [violationLetterTemplate, setViolationLetterTemplate] = useState(() => {
    try {
      return localStorage.getItem("violation_letter_template") || "1";
    } catch {
      return "1";
    }
  });

  const [ready, setReady] = useState(false);

  // Fetch from the server on mount (authoritative value beats localStorage)
  useEffect(() => {
    api
      .get("/owner-dashboard-settings")
      .then(({ data }) => {
        const term = data?.work_order_terminology;
        if (term === "heritage" || term === "standard") {
          setTerminology(term);
          localStorage.setItem("client_terminology", term);
        }

        const tpl = data?.violation_letter_template;
        if (tpl === "1" || tpl === "2") {
          setViolationLetterTemplate(tpl);
          localStorage.setItem("violation_letter_template", tpl);
        }
      })
      .catch(() => {
        // API unavailable. keep localStorage value
      })
      .finally(() => setReady(true));
  }, []);

  const updateTerminology = useCallback(async (val) => {
    setTerminology(val);
    localStorage.setItem("client_terminology", val);
    try {
      await api.put("/owner-dashboard-settings", { work_order_terminology: val });
    } catch (err) {
      console.warn("Failed to persist terminology setting:", err);
    }
  }, []);

  const updateViolationLetterTemplate = useCallback(async (val) => {
    setViolationLetterTemplate(val);
    localStorage.setItem("violation_letter_template", val);
    try {
      await api.put("/owner-dashboard-settings", { violation_letter_template: val });
    } catch (err) {
      console.warn("Failed to persist violation letter template setting:", err);
    }
  }, []);

  return (
    <ClientConfigContext.Provider
      value={{
        terminology,
        isHeritage: terminology === "heritage",
        updateTerminology,
        violationLetterTemplate,
        updateViolationLetterTemplate,
        ready,
      }}
    >
      {children}
    </ClientConfigContext.Provider>
  );
};

export const useClientConfig = () => useContext(ClientConfigContext);
